import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { backendApis } from '@/backendApis';

import { uploadFeedbackAttachmentToS3 } from '../app-logs/app-logs.utils';

import {
  type CalendarIssueType,
  Feedback,
  type FeedbackType,
} from './feedback.types';

const CALENDAR_ISSUE_TYPE_LABELS: Record<CalendarIssueType, string> = {
  event_missing: 'Event missing on the meeting notes page/Glance',
  event_incorrect: 'Event incorrect on the meeting notes page/Glance',
  dates_wrong: 'Dates are wrong in chat or routine',
  other: 'Other',
};

export const CALENDAR_ISSUE_OPTIONS = Object.entries(
  CALENDAR_ISSUE_TYPE_LABELS
) as [CalendarIssueType, string][];

export function calendarIssueTypeLabel(issueType: CalendarIssueType): string {
  return CALENDAR_ISSUE_TYPE_LABELS[issueType];
}

export function isCalendarIssueType(value: string): value is CalendarIssueType {
  return value in CALENDAR_ISSUE_TYPE_LABELS;
}

export function showLocalEventsOption(
  issueType: CalendarIssueType | null
): boolean {
  return (
    issueType === 'event_missing' ||
    issueType === 'event_incorrect' ||
    issueType === 'other'
  );
}

type CalendarEvent = Awaited<
  ReturnType<typeof backendApis.calendar.getEvents>
>[number];

// Drops free text, join links and avatars, which aren't needed to debug event sync
export function toCalendarDebugEvent(event: CalendarEvent) {
  return {
    id: event.id,
    status: event.status,
    summary: event.summary,
    start_datetime: event.start_datetime,
    end_datetime: event.end_datetime,
    is_all_day: event.is_all_day,
    shows_as_free: event.shows_as_free,
    eventType: event.eventType,
    conference: event.conferenceData && {
      type: event.conferenceData.conferenceSolution?.key.type,
      hasConference: event.conferenceData.hasConference,
    },
    attendees: event.attendees?.map((attendee) => ({
      email: attendee.email,
      displayName: attendee.displayName,
      responseStatus: attendee.responseStatus,
      self: attendee.self,
      organizer: attendee.organizer,
      resource: attendee.resource,
      optional: attendee.optional,
    })),
  };
}

export const CALENDAR_DATA_SECTION_TITLE =
  'Calendar Data (S3 key, logs bucket)';

export async function uploadLocalCalendarEvents(): Promise<string> {
  const events = await backendApis.calendar.getEvents();
  const eventsFile = new File(
    [JSON.stringify(events.map(toCalendarDebugEvent), null, 2)],
    'calendar-events.json',
    { type: 'application/json' }
  );
  return uploadFeedbackAttachmentToS3(eventsFile);
}

interface UseCalendarIssueOptions {
  feedbackType: FeedbackType;
}

export function useCalendarIssue({ feedbackType }: UseCalendarIssueOptions) {
  const isActive = feedbackType === Feedback.CalendarIssue;
  const [issueType, setIssueType] = useState<CalendarIssueType | null>(null);
  const [includeLocalEvents, setIncludeLocalEvents] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const prevFeedbackTypeRef = useRef(feedbackType);

  useEffect(() => {
    if (prevFeedbackTypeRef.current !== feedbackType) {
      prevFeedbackTypeRef.current = feedbackType;
      if (!isActive) {
        setIssueType(null);
        setIncludeLocalEvents(false);
        setUploadedFile(null);
      }
    }
  }, [feedbackType, isActive]);

  const uploadCalendarAttachments = useCallback(async (): Promise<string[]> => {
    if (!isActive) return [];

    const uploads: Promise<string>[] = [];

    if (includeLocalEvents && showLocalEventsOption(issueType)) {
      uploads.push(uploadLocalCalendarEvents());
    }

    if (uploadedFile) {
      uploads.push(uploadFeedbackAttachmentToS3(uploadedFile));
    }

    if (uploads.length === 0) return [];

    setIsUploading(true);
    try {
      const results = await Promise.allSettled(uploads);
      const urls = results
        .filter(
          (r): r is PromiseFulfilledResult<string> => r.status === 'fulfilled'
        )
        .map((r) => r.value);
      if (urls.length < results.length) {
        toast.error(
          'Some calendar attachments failed to upload, but your feedback will still be sent'
        );
      }
      return urls;
    } finally {
      setIsUploading(false);
    }
  }, [isActive, includeLocalEvents, issueType, uploadedFile]);

  return {
    issueType,
    setIssueType,
    includeLocalEvents,
    setIncludeLocalEvents,
    uploadedFile,
    setUploadedFile,
    uploadCalendarAttachments,
    isUploading,
    showLocalEventsOption: showLocalEventsOption(issueType),
  };
}
